import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import DataModUtil from '@/lib/utils/DataModUtil';
import { handleApiError } from '@/utils/apiErrorHandler';
import { clearError } from './errorSlice';

// Define interfaces for state and payloads
interface DesignationInfo {
  version?: string;
  publishedBegin?: string;
  publishedEnd?: string;
  [key: string]: any;
}

interface DesignationOverviewState {
  designations: Record<string, DesignationInfo>;
  selectOptions: Array<{ value: string; label: string }>;
  physician: Record<string, any>;
  odmDesignationPhase: Record<string, any>;
  physicianDesignations: Record<string, DesignationInfo>;
  physicianSelectOptions: Array<{ value: string; label: string }>;
  physicianInfo: Record<string, any>;
  physicianOdmDesignationPhase: Record<string, any>;
  loading: boolean;
  error: string | null;
  physicianDesignationsLoading: boolean
}

const initialState: DesignationOverviewState = {
  designations: {},
  selectOptions: [],
  physician: {},
  odmDesignationPhase: {},
  physicianDesignations: {},
  physicianSelectOptions: [],
  physicianInfo: {},
  physicianOdmDesignationPhase: {},
  loading: false,
  error: null,
  physicianDesignationsLoading: false
};

// Define async thunks for fetching data from API endpoints directly
export const fetchDesignationOverview = createAsyncThunk(
  'designationOverview/fetchDesignationOverview',
  async (payload: { mpin?: string | number; username: string; isDelegate?: boolean }, { rejectWithValue, dispatch }) => {
    const routeUrl =
      payload.mpin && payload.isDelegate
        ? `/papi/secure/physician/${payload.mpin}/designation-overview`
        : "/papi/secure/designation-overview";

    try {
      dispatch(clearError());
      const designationResponse = await fetch(routeUrl, { cache: 'no-store', credentials: 'include' })
        .then(res => {
          if (!res.ok) throw new Error(`Failed to fetch designation data: ${res.status} ${res.statusText}`);
          return res.json();
        });

      const designationData = designationResponse.data
        ? DataModUtil.getDesignationOverview(designationResponse.data)
        : {
          designations: {},
          selectOptions: [],
          physician: {},
          odmDesignationPhase: {},
          physicianDesignations: {},
          physicianSelectOptions: [],
          physicianInfo: {},
          physicianOdmDesignationPhase: {},
        };
      return {
        ...designationData,
        isDelegate: payload.isDelegate || false
      };
    } catch (error: any) {
      handleApiError(error as any, "designationOverview", dispatch);
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  }
);

// Create slice with more detailed reducer logic
const designationOverviewSlice = createSlice({
  name: 'designationOverview',
  initialState,
  reducers: {
    clearDesignationOverview: (state) => {
      state.designations = {};
      state.selectOptions = [];
      state.physician = {};
      state.odmDesignationPhase = {};
      state.physicianDesignations = {};
      state.physicianSelectOptions = [];
      state.physicianInfo = {};
      state.physicianOdmDesignationPhase = {};
      state.loading = false;
      state.error = null;
      state.physicianDesignationsLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // ...existing code...
      .addCase(fetchDesignationOverview.pending, (state, action) => {
        state.designations = {};
        state.selectOptions = [];
        state.physician = {};
        state.odmDesignationPhase = {};
        state.physicianDesignations = {};
        state.physicianSelectOptions = [];
        state.physicianInfo = {};
        state.physicianOdmDesignationPhase = {};
        state.loading = true;
        state.error = null;
        if (action.meta.arg.isDelegate) {
          state.physicianDesignationsLoading = true;
        } else {
          state.loading = true;
        }
      })
      .addCase(fetchDesignationOverview.fulfilled, (state, action) => {
        const designationsObj: Record<string, DesignationInfo> = {};
        ['current', 'future'].forEach(k => {
          const thisDesignation = action.payload.designations[k];

          if (thisDesignation && thisDesignation.publishedEnd?.includes('9999')) {
            designationsObj[k] = thisDesignation;
          }
        });
        const selectOptionsArr = action.payload.selectOptions.filter(
          o => designationsObj[o.value]
        );

        if (action.payload.isDelegate) {
          // Store in physician-specific state
          state.physicianDesignations = designationsObj;
          state.physicianSelectOptions = selectOptionsArr;
          state.physicianInfo = action.payload.physician;
          state.physicianOdmDesignationPhase = action.payload.odmDesignationPhase;
          state.physicianDesignationsLoading = false;
        } else {
          // Store in general state
          state.designations = designationsObj;
          state.selectOptions = selectOptionsArr;
          state.physician = action.payload.physician;
          state.odmDesignationPhase = action.payload.odmDesignationPhase;
          state.loading = false;
        }
      })
      .addCase(fetchDesignationOverview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'An unknown error occurred';
        state.designations = {};
        state.selectOptions = [];
        state.physician = {};
        state.odmDesignationPhase = {};
        state.physicianDesignations = {};
        state.physicianSelectOptions = [];
        state.physicianInfo = {};
        state.physicianOdmDesignationPhase = {};
        state.physicianDesignationsLoading = false;
      });
  },
});

export const { clearDesignationOverview } = designationOverviewSlice.actions;

export default designationOverviewSlice.reducer;
